import type { BatchRecord, ExpiryClass } from "../types";

export const EXPIRY_CYCLE_DAYS = 365;

const EXPIRY_BOUNDARIES = {
  quarter: 91.25,
  third: 121.67,
  half: 182.5,
} as const;

export function shanghaiDateString(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";
  return `${value("year")}-${value("month")}-${value("day")}`;
}

function validCalendarDate(year: number, month: number, day: number) {
  if (
    !Number.isInteger(year)
    || !Number.isInteger(month)
    || !Number.isInteger(day)
  ) return "";
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year
    || date.getUTCMonth() !== month - 1
    || date.getUTCDate() !== day
  ) return "";
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function dateFromValue(value?: string, allowMonthOnly = false) {
  const raw = value?.trim() ?? "";
  if (!raw) return "";

  const calendar = raw.match(/^(\d{4})[-/.](\d{1,2})(?:[-/.](\d{1,2}))?/);
  if (calendar) {
    const year = Number(calendar[1]);
    const month = Number(calendar[2]);
    if (!calendar[3] && !allowMonthOnly) return "";
    const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const day = calendar[3] ? Number(calendar[3]) : lastDay;
    return validCalendarDate(year, month, day);
  }

  const compact = raw.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (compact) {
    return validCalendarDate(
      Number(compact[1]),
      Number(compact[2]),
      Number(compact[3]),
    );
  }

  return "";
}

export function remainingBatchDays(expiresOn: string, today = shanghaiDateString()) {
  const start = Date.parse(`${today}T00:00:00+08:00`);
  const end = Date.parse(`${expiresOn}T00:00:00+08:00`);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return Math.ceil((end - start) / 86_400_000);
}

export function expiryClassLabel(value: ExpiryClass) {
  if (value === "quarter") return "¼ 四分之一临期";
  if (value === "third") return "⅓ 三分之一临期";
  if (value === "half") return "½ 二分之一临期";
  if (value === "fresh") return "新鲜期";
  if (value === "expired") return "已过期";
  if (value === "unknown") return "待核对";
  return "无临期";
}

export function classifyExpiryRatio(ratio: number | null): ExpiryClass {
  if (ratio == null || !Number.isFinite(ratio)) return "unknown";
  if (ratio <= 0) return "expired";
  if (ratio <= EXPIRY_BOUNDARIES.quarter / EXPIRY_CYCLE_DAYS) return "quarter";
  if (ratio <= EXPIRY_BOUNDARIES.third / EXPIRY_CYCLE_DAYS) return "third";
  if (ratio <= EXPIRY_BOUNDARIES.half / EXPIRY_CYCLE_DAYS) return "half";
  return "fresh";
}

export interface BatchExpirySnapshot {
  expiresOn: string;
  producedOn: string;
  shelfLifeDays: number | null;
  remainingDays: number | null;
  remainingRatio: number | null;
  expiryClass: ExpiryClass;
  basis: "batch-date" | "lot-date" | "incomplete";
}

/**
 * 批次日期就是到期日。生产日期和保质期天数仅作为导入元数据保留，
 * 不参与临期分级。等级统一按剩余天数 / 365 天参考周期计算。
 */
export function batchExpirySnapshot(
  batch: Pick<BatchRecord, "expiresOn" | "lot" | "producedOn" | "shelfLifeDays">,
  today = shanghaiDateString(),
): BatchExpirySnapshot {
  const importedExpiry = dateFromValue(batch.expiresOn, true);
  const lotExpiry = dateFromValue(batch.lot);
  const expiresOn = importedExpiry || lotExpiry;
  const producedOn = dateFromValue(batch.producedOn);
  const shelfLifeDays = Number(batch.shelfLifeDays);
  const remainingDays = expiresOn ? remainingBatchDays(expiresOn, today) : null;
  const remainingRatio = remainingDays == null
    ? null
    : Math.max(-1, remainingDays / EXPIRY_CYCLE_DAYS);

  return {
    expiresOn,
    producedOn,
    shelfLifeDays: Number.isFinite(shelfLifeDays) && shelfLifeDays > 0
      ? shelfLifeDays
      : null,
    remainingDays,
    remainingRatio,
    expiryClass: classifyExpiryRatio(remainingRatio),
    basis: importedExpiry
      ? "batch-date"
      : lotExpiry
        ? "lot-date"
        : "incomplete",
  };
}

export function expiryUrgency(value: ExpiryClass) {
  if (value === "expired") return 0;
  if (value === "quarter") return 1;
  if (value === "third") return 2;
  if (value === "half") return 3;
  if (value === "fresh") return 4;
  if (value === "none") return 5;
  return 6;
}

export function resolveBatchExpiry(batch: Pick<BatchRecord, "expiresOn" | "lot">) {
  return dateFromValue(batch.expiresOn, true) || dateFromValue(batch.lot);
}
